<template>
  <m-input-group
    class="m-quantity"
    horizontal
    :label="label"
    :helper="helper"
    :instructions="instructions"
    left
  >
    <template v-if="$slots.helper" #helper> <slot name="helper" /></template>

    <!-- NOTE `@update:modelValue="$forceUpdate"` works around the number input not
    refreshing when the model changes externally. -->
    <m-input
      v-bind="validProps"
      v-model="formValue.value"
      :name="`${name}.value`"
      type="number"
      label="Value"
      small
      @update:model-value="$forceUpdate"
    />
    <m-simple-select
      v-model="formValue.unit"
      :name="`${name}.unit`"
      :options="units"
      label="Unit"
      placeholder="Unit"
    />
  </m-input-group>
</template>

<script lang="ts">
import { PropType, WritableComputedRef, computed, defineComponent, provide, watch } from "vue";
import { useFormValue } from "../../../../composables/namedValue";
import MInput from "./../MInput";
import MInputGroup from "./../../MInputGroup";
import MSimpleSelect from "./../MSimpleSelect";

export default defineComponent({
  components: {
    MInput,
    MInputGroup,
    MSimpleSelect,
  },
  inheritAttrs: false,
  props: {
    ...((MInput as any).props as {
      label: PropType<string>;
      name: PropType<string>;
      modelValue: {
        type: () => { value: number; unit: string };
        required: true;
        default: () => { value: undefined; unit: undefined };
      };
    }),
    modelValue: {
      type: Object as () => { value: number; unit: string },
      required: false,
      default: () => ({
        value: undefined,
        unit: undefined,
      }),
    },

    units: {
      type: Array as () => Array<{ label: string; value: string }>,
      // MSimpleSelect's `options` is required — default to [] so `:options="units"`
      // is never `undefined`.
      default: () => [],
    },
    helper: String,
    instructions: String,
  },

  setup(props, { attrs }) {
    provide("input-dont-use-name", true);

    const formValue = useFormValue(props) as WritableComputedRef<{ value: number; unit: string }>;

    // eslint-disable-next-line no-undef
    if (IS_APP && import.meta.env.NODE_ENV !== "production") {
      watch(formValue, (v) => {
        if (!v) {
          console.error("Please set a value to: ", props.name);
        }
      });
    }

    // label / helper / instructions belong on the group; name / modelValue / units
    // are handled explicitly. Everything else (required, disable, min, max, clearable,
    // …) passes through to the quantity input.
    const validProps = computed(() => {
      const p = {
        ...attrs,
        ...props,
      } as Record<string, unknown>;

      delete p.name;
      delete p.modelValue;
      delete p["onUpdate:modelValue"];
      delete p.units;
      delete p.label;
      delete p.helper;
      delete p.instructions;

      return p;
    });

    return {
      formValue,
      validProps,
    };
  },
});
</script>
